import React from 'react';
import { FC, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

type MonthRangeInputProps = {
  disabled?: boolean;
  isClearable?: boolean;
  maxDate?: Date | null;
  minDate?: Date | null;
  onChange: (dates: { startDate: Date | null; endDate: Date | null }) => void;
  rangeLimit?: number;
  value: { startDate: Date | null; endDate: Date | null };
  highlightPlaceholder?: boolean;
};

const MonthRangeInput: FC<MonthRangeInputProps> = ({
  disabled = false,
  isClearable: clearButton = false,
  maxDate,
  minDate,
  onChange,
  rangeLimit,
  value,
  highlightPlaceholder = false,
}: MonthRangeInputProps) => {
  const [calculatedMaxDate, setCalculatedMaxDate] = useState<Date | null>(null);

  useEffect(() => {
    if (value.startDate && rangeLimit) {
      const maxAllowedDate = new Date(Date.UTC(value.startDate.getUTCFullYear() + rangeLimit, value.startDate.getUTCMonth(), 1));
      setCalculatedMaxDate(maxAllowedDate);
    } else {
      setCalculatedMaxDate(null);
    }
  }, [value.startDate, rangeLimit]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [startDate, endDate] = dates;
    const maxAllowedEndDate = calculatedMaxDate ?? maxDate;

    let normalizedStartDate = startDate ? new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 1)) : null;
    let normalizedEndDate = endDate ? new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth() + 1, 0)) : null;

    if (normalizedStartDate && normalizedEndDate) {
      if (maxAllowedEndDate && normalizedEndDate > maxAllowedEndDate) {
        onChange({ startDate: normalizedStartDate, endDate: maxAllowedEndDate });
      } else {
        onChange({ startDate: normalizedStartDate, endDate: normalizedEndDate });
      }
    } else {
      onChange({ startDate: normalizedStartDate, endDate: null });
    }
  };

  const handleCalendarClose = () => {
    if (value.startDate && !value.endDate) {
      onChange({ startDate: null, endDate: null });
    }
  };

  const CustomInput = React.forwardRef<HTMLInputElement>((props, ref) => <input {...props} ref={ref} readOnly />);

  const effectiveMinDate =
    minDate === undefined ? new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)) : minDate ?? undefined;

  return (
    <div className="date-range-input">
      <DatePicker
        placeholderText="Select months"
        selected={value.startDate}
        onChange={dates => handleDateChange(dates as [Date | null, Date | null])}
        startDate={value.startDate}
        endDate={value.endDate}
        selectsRange
        maxDate={calculatedMaxDate ?? maxDate ?? undefined}
        showYearDropdown
        showMonthYearPicker
        minDate={effectiveMinDate}
        isClearable={clearButton}
        disabled={disabled}
        dateFormat="MMM yyyy"
        clearButtonClassName="date-range-input-clear-button"
        onCalendarClose={handleCalendarClose}
        customInput={<CustomInput />}
        className={highlightPlaceholder ? 'highlight-placeholder' : ''}
      />
    </div>
  );
};

export default MonthRangeInput;
